import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Users, Music, Sparkles, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_clients: 0, total_playlists: 0, total_generated: 0 });
  const [recentClients, setRecentClients] = useState([]);
  const [recentPlaylists, setRecentPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, clientsRes, playlistsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/admin/clients`),
          axios.get(`${API}/admin/playlists`)
        ]);
        setStats(statsRes.data);
        setRecentClients(clientsRes.data.slice(0, 5));
        setRecentPlaylists(playlistsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 overflow-y-auto content-scroll" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-extrabold text-white tracking-wider mb-2" style={{ fontFamily: 'Outfit' }}>
            Welcome, {user?.name}
          </h1>
          <p className="text-[#606060]">Manage your venues and curated playlists</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          <div className="glass-surface rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#FF007F]/15 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#FF007F]" />
              </div>
              <span className="text-3xl font-extrabold text-white">{stats.total_clients}</span>
            </div>
            <h3 className="text-[#A0A0A0] font-semibold text-sm tracking-wide">Active Venues</h3>
          </div>

          <div className="glass-surface rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#39FF14]/15 flex items-center justify-center">
                <Music className="w-6 h-6 text-[#39FF14]" />
              </div>
              <span className="text-3xl font-extrabold text-white">{stats.total_playlists}</span>
            </div>
            <h3 className="text-[#A0A0A0] font-semibold text-sm tracking-wide">Playlists</h3>
          </div>

          <div className="glass-surface rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#00E676]/15 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#00E676]" />
              </div>
              <span className="text-3xl font-extrabold text-white">{stats.total_generated}</span>
            </div>
            <h3 className="text-[#A0A0A0] font-semibold text-sm tracking-wide">Generated</h3>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          <Link
            to="/admin/clients"
            className="group p-6 glass-surface rounded-2xl transition-all hover:border-[#FF007F]/30 card-hover"
            data-testid="manage-clients-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#FF007F]/15 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-7 h-7 text-[#FF007F]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white tracking-wide" style={{ fontFamily: 'Outfit' }}>Manage Venues</h3>
                <p className="text-[#606060] text-sm">Add, edit, or remove venue accounts</p>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/playlists"
            className="group p-6 glass-surface rounded-2xl transition-all hover:border-[#39FF14]/30 card-hover"
            data-testid="manage-playlists-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#39FF14]/15 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Music className="w-7 h-7 text-[#39FF14]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white tracking-wide" style={{ fontFamily: 'Outfit' }}>Manage Playlists</h3>
                <p className="text-[#606060] text-sm">Create and assign playlists to venues</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="glass-surface rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold text-white tracking-wider uppercase">Recent Venues</h3>
              <Link to="/admin/clients" className="text-xs text-[#FF007F] hover:underline font-semibold">View all</Link>
            </div>
            {recentClients.length > 0 ? (
              <div className="space-y-3">
                {recentClients.map((client, index) => (
                  <div key={client.id || index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#FF007F]/10 flex items-center justify-center text-[#FF007F] font-bold text-sm">
                        {client.name?.charAt(0) || 'V'}
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{client.name}</p>
                        <p className="text-[#606060] text-xs">{client.email}</p>
                      </div>
                    </div>
                    <span className={`tag text-[9px] ${client.is_active !== false ? 'tag-green' : 'tag-red'}`}>
                      {client.is_active !== false ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#606060] text-center py-4 text-sm">No venues yet</p>
            )}
          </div>

          <div className="glass-surface rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold text-white tracking-wider uppercase">Recent Playlists</h3>
              <Link to="/admin/playlists" className="text-xs text-[#FF007F] hover:underline font-semibold">View all</Link>
            </div>
            {recentPlaylists.length > 0 ? (
              <div className="space-y-3">
                {recentPlaylists.map((playlist, index) => (
                  <div key={playlist.id || index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {playlist.cover_image ? (
                        <img src={playlist.cover_image} alt={playlist.name} className="w-9 h-9 rounded-lg object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                          <Music className="w-4 h-4 text-[#606060]" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium text-sm">{playlist.name}</p>
                        <p className="text-[#606060] text-xs">{playlist.assigned_clients?.length || 0} venues</p>
                      </div>
                    </div>
                    {playlist.mood && <span className="tag tag-purple text-[9px]">{playlist.mood}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#606060] text-center py-4 text-sm">No playlists yet</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
